/**
 * Match events — mid-game dynamic events (solar flares, asteroid passes, bounties, etc.)
 * Pure functions — no side effects, no rendering. Deterministic given same inputs.
 */

import type {
  GameState, MatchEventState, MatchEvent, MatchEventType,
  ModeConfig, Vec3,
} from '../types';
import { SeededRandom, keyedRandom } from './random';
import {
  EVENT_MIN_TURN,
  EVENT_INTERVAL_RARE,
  EVENT_INTERVAL_NORMAL,
  EVENT_INTERVAL_FREQUENT,
  SOLAR_FLARE_DURATION,
  ASTEROID_PASS_DURATION,
  COMMS_BLACKOUT_DURATION,
  PPC_INSPECTION_CBP_BONUS,
  BOUNTY_SURVEY_CBP,
  BOUNTY_PRESERVE_CBP,
  BOUNTY_SURVEY_TURNS,
  ASTEROID_MASS,
} from '../constants';
import { dcos, dsin } from './dmath';
import { inTurns, seconds } from './duration';

/** Ticks between event-clock steps on the action clock (once a second: events last turns, 15 s each). */
export const ACTION_EVENT_PERIOD_TICKS = 120;

/** R14 (a): the action clock's event clock — elapsed seconds in the turn family events are scheduled in. */
export function actionEventClock(actionSeconds: number): number {
  return inTurns(seconds(actionSeconds));
}

// =============================================================================
// EVENT POOL
// =============================================================================

const ENVIRONMENT_EVENTS: MatchEventType[] = [
  // R14 (d): the debris field is out of the pool — it never did anything, and making it a hazard to flights means
  // the shot simulation in the kernel and JS (the asteroid pass's parity work); the type stays so old saves still read
  'solarFlare', 'asteroidPass', 'commsBlackout',
];

const BOUNTY_EVENTS: MatchEventType[] = [
  'ppcInspection', 'bounty_survey', 'bounty_preserve',
];

function getEventDuration(type: MatchEventType): number {
  switch (type) {
    case 'solarFlare': return SOLAR_FLARE_DURATION;
    case 'asteroidPass': return ASTEROID_PASS_DURATION;
    case 'commsBlackout': return COMMS_BLACKOUT_DURATION;
    case 'debrisField': return 3;
    case 'bounty_survey': return BOUNTY_SURVEY_TURNS;   // R14 (c): a window to fly a probe over the target
    default: return 0; // instant events (PPC, the preserve bounty)
  }
}

function getEventInterval(frequency: 'rare' | 'normal' | 'frequent'): number {
  switch (frequency) {
    case 'rare': return EVENT_INTERVAL_RARE;
    case 'normal': return EVENT_INTERVAL_NORMAL;
    case 'frequent': return EVENT_INTERVAL_FREQUENT;
  }
}

// =============================================================================
// INIT
// =============================================================================

/**
 * Pre-schedule events deterministically from seed.
 * Events are placed at intervals with jitter, types drawn from pool.
 */
export function initMatchEventState(
  config: ModeConfig,
  rng: SeededRandom,
): MatchEventState {
  if (config.eventsEnabled === false) {
    return { events: [], nextEventId: 0 };
  }

  const frequency = config.eventFrequency ?? 'normal';
  const interval = getEventInterval(frequency);
  const events: MatchEvent[] = [];
  let nextId = 0;
  let turn = EVENT_MIN_TURN;
  const maxTurn = config.turnLimit ?? 200;

  while (turn < maxTurn) {
    // Jitter: +/- 25% of interval
    const jitter = Math.floor((rng.next() - 0.5) * interval * 0.5);
    const triggerTurn = turn + jitter;

    if (triggerTurn >= EVENT_MIN_TURN && triggerTurn < maxTurn) {
      // Alternate between environment and bounty events
      const pool = rng.next() < 0.6 ? ENVIRONMENT_EVENTS : BOUNTY_EVENTS;
      const typeIdx = Math.floor(rng.next() * pool.length);
      const type = pool[typeIdx];
      const duration = getEventDuration(type);

      events.push({
        id: nextId++,
        type,
        triggerTurn,
        duration,
        active: false,
        expired: false,
      });
    }

    turn += interval;
  }

  return { events, nextEventId: nextId };
}

// =============================================================================
// TICK
// =============================================================================

/**
 * Activate events at their trigger turn, expire after duration.
 * Process instant events (PPC inspection, bounties).
 *
 * R14 (a): events are scheduled in turns (a trigger turn, a duration), and `now` is the event clock in that family:
 * the turn number on the turn clock (the default), the elapsed action seconds converted once by SECONDS_PER_TURN on
 * the action clock (`actionEventClock`, decision 110) — never a counter of the other clock.
 */
export function tickMatchEvents(state: GameState, now: number = state.turnNumber): GameState {
  const eventState = state.matchEvents;
  if (!eventState || eventState.events.length === 0) return state;

  const turn = now;
  let updatedState = state;
  const updatedEvents = eventState.events.map(event => {
    if (event.expired) return event;

    // Activate at trigger turn
    if (!event.active && turn >= event.triggerTurn) {
      const activated = { ...event, active: true };

      // Process instant events
      if (event.duration === 0) {
        if (event.type === 'ppcInspection') {
          updatedState = processPPCInspection(updatedState);
        }
        // R14 (c): the preserve bounty pays every team that has burned no flora, through the flora-damage record
        if (event.type === 'bounty_preserve') updatedState = processBounty(updatedState, event);
        return { ...activated, expired: true };
      }

      // R14 (c): a survey bounty names its target planet when it opens (a keyed draw: the same on every peer)
      if (event.type === 'bounty_survey') {
        const target = surveyTarget(updatedState, event);
        return target === null ? { ...activated, expired: true, active: false } : { ...activated, data: { ...(event.data ?? {}), targetPlanetIndex: target } };
      }

      return activated;
    }

    // Expire duration events
    if (event.active && turn >= event.triggerTurn + event.duration) {
      return { ...event, expired: true, active: false };
    }

    return event;
  });

  return {
    ...updatedState,
    matchEvents: { ...eventState, events: updatedEvents },
  };
}

// =============================================================================
// QUERY HELPERS
// =============================================================================

/**
 * True during an active solar flare — disables trajectory preview.
 */
export function isTrajectoryPreviewDisabled(state: GameState): boolean {
  return hasActiveEvent(state, 'solarFlare');
}

/**
 * True during an active comms blackout — disables bearing alerts.
 */
export function isBearingAlertDisabled(state: GameState): boolean {
  return hasActiveEvent(state, 'commsBlackout');
}

function hasActiveEvent(state: GameState, type: MatchEventType): boolean {
  if (!state.matchEvents) return false;
  return state.matchEvents.events.some(e => e.type === type && e.active && !e.expired);
}

/**
 * Get position and mass of active asteroid for gravity computation.
 * Deterministic orbit based on turn number.
 */
export function getActiveAsteroid(state: GameState): { position: Vec3; mass: number } | null {
  if (!state.matchEvents) return null;

  const asteroidEvent = state.matchEvents.events.find(
    e => e.type === 'asteroidPass' && e.active && !e.expired
  );
  if (!asteroidEvent) return null;

  // Deterministic circular orbit around the system center
  const elapsedTurns = state.turnNumber - asteroidEvent.triggerTurn;
  const angle = (elapsedTurns / ASTEROID_PASS_DURATION) * Math.PI;
  const orbitRadius = 40;

  return {
    position: {
      x: orbitRadius * dcos(angle),
      y: 5,
      z: orbitRadius * dsin(angle),
    },
    mass: ASTEROID_MASS,
  };
}

// =============================================================================
// EVENT EFFECTS
// =============================================================================

/**
 * PPC Inspection: award bonus CBP to team with least flora destruction.
 */
export function processPPCInspection(state: GameState): GameState {
  const flora = state.floraDestruction;
  if (!flora) return state;

  // Find team with least destruction
  const teamIndices = new Set(state.players.map(p => p.teamIndex));
  let bestTeam = -1;
  let bestDestruction = Infinity;

  for (const ti of teamIndices) {
    const destroyed = flora.perTeam[ti] ?? 0;
    if (destroyed < bestDestruction) {
      bestDestruction = destroyed;
      bestTeam = ti;
    }
  }

  if (bestTeam < 0) return state;

  const updatedPlayers = state.players.map(p => {
    if (p.alive && p.teamIndex === bestTeam) {
      return { ...p, cbp: p.cbp + PPC_INSPECTION_CBP_BONUS };
    }
    return p;
  });

  return { ...state, players: updatedPlayers };
}

/**
 * Bounty survey: award CBP to first team that probed a target planet this turn.
 * Bounty preserve: award CBP to teams with zero flora destruction this turn.
 * These are meant to be called from game logic when bounty events fire.
 */
export function processBounty(
  state: GameState,
  event: MatchEvent,
): GameState {
  if (event.type === 'bounty_preserve') {
    // Award CBP to all teams with zero flora destruction
    const flora = state.floraDestruction;
    if (!flora) return state;

    const teamIndices = new Set(state.players.map(p => p.teamIndex));
    const updatedPlayers = state.players.map(p => {
      if (p.alive && teamIndices.has(p.teamIndex)) {
        const destroyed = flora.perTeam[p.teamIndex] ?? 0;
        if (destroyed === 0) {
          return { ...p, cbp: p.cbp + BOUNTY_PRESERVE_CBP };
        }
      }
      return p;
    });

    return { ...state, players: updatedPlayers };
  }

  // bounty_survey: paid through the probe's reveal (claimSurveyBounty)
  return state;
}

/** R14 (c): the survey bounty's target — a living planet, drawn by the event's keys. */
function surveyTarget(state: GameState, event: MatchEvent): number | null {
  const candidates = state.planets.map((p, i) => ({ p, i })).filter(x => x.p.alive && x.p.type === 'planet').map(x => x.i);
  if (!candidates.length) return null;
  return candidates[Math.floor(keyedRandom(event.id, event.triggerTurn, 0x5012) * candidates.length) % candidates.length];
}

/** The open survey bounty and its target planet, if any. */
export function openSurveyBounty(state: Pick<GameState, 'matchEvents'>): { event: MatchEvent; targetPlanetIndex: number } | null {
  for (const e of state.matchEvents?.events ?? []) {
    if (e.type !== 'bounty_survey' || !e.active || e.expired) continue;
    const t = e.data?.targetPlanetIndex;
    if (typeof t === 'number') return { event: e, targetPlanetIndex: t };
  }
  return null;
}

/**
 * R14 (c): a probe flew over a planet — if it's the open survey bounty's target, its shooter is paid BOUNTY_SURVEY_CBP
 * and the bounty closes (the first to survey it claims it). Called from the probe's reveal (Match.revealProbePath).
 */
export function claimSurveyBounty(state: GameState, shooterId: number, surveyedPlanets: readonly number[]): GameState {
  const open = openSurveyBounty(state);
  if (!open || !surveyedPlanets.includes(open.targetPlanetIndex)) return state;
  const shooter = state.players.find(p => p.id === shooterId);
  if (!shooter) return state;
  return {
    ...state,
    players: state.players.map(p => (p.id === shooterId ? { ...p, cbp: p.cbp + BOUNTY_SURVEY_CBP } : p)),
    matchEvents: { ...state.matchEvents!, events: state.matchEvents!.events.map(e => (e.id === open.event.id ? { ...e, active: false, expired: true, data: { ...(e.data ?? {}), claimedBy: shooterId } } : e)) },
  };
}

// =============================================================================
// NOTIFICATIONS
// =============================================================================

export function generateEventNotification(event: MatchEvent): { message: string; color: string } {
  switch (event.type) {
    case 'solarFlare':
      return { message: 'Solar flare — trajectory preview offline.', color: '#ff6644' };
    case 'asteroidPass':
      return { message: 'Uncharted body entering the system — expect gravity anomalies.', color: '#aaaaff' };
    case 'commsBlackout':
      return { message: 'Comms blackout — bearing data unavailable.', color: '#888888' };
    case 'debrisField':
      return { message: 'Orbital debris field passing through.', color: '#aa8855' };
    case 'ppcInspection':
      return { message: 'Inspection — bonus to the team with the least environmental damage.', color: '#55cc55' };
    case 'bounty_survey':
      return { message: 'Bounty — probe the designated planet for bonus budget.', color: '#ffcc44' };
    case 'bounty_preserve':
      return { message: 'Bounty — zero flora damage earns bonus budget.', color: '#55cc55' };
    default:
      return { message: 'Event notification.', color: '#ffffff' };
  }
}
